// src/components/FeaturedWork.js
import React, { useEffect, useState } from "react";
import "./FeaturedWork.css";
import frame from "../assets/fw-frame.png";
import underline from "../assets/underline.png";
import { FiExternalLink } from "react-icons/fi";

import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

/** Accept:
 *  - Full URLs: https://...
 *  - Site-relative files you place under /public (e.g. /featured/services.jpg)
 *  Anything else => no background image (pattern shows), but the frame + text render.
 */
const normalizeImage = (s) =>
  typeof s === "string" && (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/"))
    ? s
    : null;

function FeaturedWork() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const col = collection(db, "homeFeatured");
        // Matches your Firestore rules (only active docs are readable)
        const snap = await getDocs(query(col, where("active", "==", true)));
        if (!mounted) return;

        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));

        // Sort by order then title
        rows.sort((a, b) => {
          const ao = Number.isFinite(a.order) ? a.order : Number(a.order) || 9999;
          const bo = Number.isFinite(b.order) ? b.order : Number(b.order) || 9999;
          return ao !== bo ? ao - bo : String(a.title || "").localeCompare(String(b.title || ""));
        });

        // Normalize images (no Storage calls, nothing async here)
        const withImgs = rows.map((r) => ({ ...r, image: normalizeImage(r.image) }));
        setWorks(withImgs);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[FeaturedWork] Firestore read failed:", e);
        }
        setWorks([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="featured-section">
      <div className="section-title centered">
        <h2>
          <span className="heading-text">
            Featured Work
            <img src={underline} alt="Underline" className="underline-img" />
          </span>
        </h2>
      </div>

      {loading ? (
        <p className="no-featured-message">Loading featured work...</p>
      ) : works.length === 0 ? (
        <p className="no-featured-message">
          <em>No featured work just yet. Come back soon to see something awesome!</em>
        </p>
      ) : (
        <div className="work-cards">
          {works.map((work) => (
            <div className="work-card" key={work.id}>
              {/* The frame is always visible */}
              <img src={frame} alt="frame" className="frame-img" />
              <div className="work-content">
                {/* If no image, the CSS pattern shows; the frame + text still render */}
                <div
                  className="placeholder-box"
                  style={{
                    backgroundImage: work.image ? `url(${work.image})` : undefined,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div className="work-text">
                  <h3>{work.title || "Untitled"}</h3>
                  {work.description && <p>{work.description}</p>}
                </div>
                {work.link && (
                  <a
                    href={work.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link-icon"
                    aria-label="Open project"
                  >
                    <FiExternalLink />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FeaturedWork;
